"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Popper = Popper;
/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const react_1 = __importDefault(require("react"));
const styled_components_1 = require("../styled-components");
const react_2 = require("@floating-ui/react");
const react_i18next_1 = require("react-i18next");
const util_1 = require("../util");
const PortalWrapper = styled_components_1.styled.div `
  ${({ height, maxHeight, width, maxWidth, theme }) => ({
    maxHeight,
    maxWidth,
    height: height !== null && height !== void 0 ? height : "fit-content",
    width: typeof width === "string"
        ? width
        : typeof width === "number"
            ? // Add 10px to compensate for the padding around it
                width + 10
            : "max-content",
    color: theme.popup.fg,
    borderRadius: theme.radius,
})}
  position: fixed;
  overflow: hidden;
  z-index: 98;
  display: flex;
  flex-direction: column;
  // Padding lets the box-shadow show, rather than getting cut off
  padding: 5px;
  // Floating UI gives the popup a tabIndex, but there's no point in showing focus on it
  &:focus {
    outline: none;
  }
`;
/** Element for positioning a popup, using Floating UI (formerly known as Popper)
 */
function Popper({ children, open, referenceElement, height, maxHeight, width, maxWidth, referencePosition, onClose, }) {
    // Store current position to avoid unnecessary updates (potentially infinite loops)
    const [currentPosition, setCurrentPosition] = react_1.default.useState(undefined);
    const { refs, floatingStyles, context } = (0, react_2.useFloating)({
        open,
        strategy: "fixed",
        placement: "bottom-start",
        onOpenChange: (open) => {
            if (!open && onClose)
                onClose();
        },
        elements: {
            reference: referenceElement,
        },
        middleware: [
            (0, react_2.offset)({
                // The popup has 5px padding outside the box to allow for shadows.
                // The default {mainAxis: 5, crossAxis: -5} aligns the popup box corner 10px below the ref and in line with its left edge.
                mainAxis: 5,
                crossAxis: -5,
            }),
            (0, react_2.shift)(),
            (0, react_2.flip)(),
        ],
    });
    // Allow custom positioning
    if (!(0, util_1.arraysMatch)(referencePosition, currentPosition)) {
        setCurrentPosition(referencePosition);
        if (referencePosition) {
            const virtualElement = {
                getBoundingClientRect: () => ({
                    x: referencePosition[0],
                    y: referencePosition[1],
                    left: referencePosition[0],
                    top: referencePosition[1],
                    bottom: referencePosition[1],
                    right: referencePosition[0],
                    width: 0,
                    height: 0,
                }),
            };
            refs.setPositionReference(virtualElement);
        }
        else {
            refs.setPositionReference(null);
        }
    }
    // Close popup on background click, reference click or Escape key
    const dismiss = (0, react_2.useDismiss)(context, {
        referencePress: true,
        // Prevent clicking on one popup closing another - see https://floating-ui.com/docs/useDismiss#outsidepress
        outsidePress: (evt) => { var _a; return !((_a = evt.target) === null || _a === void 0 ? void 0 : _a.closest(".popup-wrapper")); },
    });
    const { getFloatingProps } = (0, react_2.useInteractions)([dismiss]);
    const { t } = (0, react_i18next_1.useTranslation)();
    return open ? (react_1.default.createElement(react_2.FloatingPortal, null,
        react_1.default.createElement(react_2.FloatingFocusManager, { context: context, visuallyHiddenDismiss: t("button-Close") },
            react_1.default.createElement(PortalWrapper, { ref: refs.setFloating, height: height, maxHeight: maxHeight, width: width, maxWidth: maxWidth, style: {
                    ...floatingStyles,
                }, ...getFloatingProps }, children)))) : null;
}
//# sourceMappingURL=Popper.js.map